import { useState, useEffect } from "react";
import axios from "axios";
import { motion, useScroll, useTransform, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { Phone, MessageCircle, MapPin, Mail, Clock, ArrowUp, Check, Loader2, Send, Instagram, Facebook } from "lucide-react";
import { BUSINESS, SERVICE_OPTIONS } from "@/data";
import { Reveal, Eyebrow, Magnetic } from "@/components/site/motion-lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Final CTA ---------------- */
export function FinalCta() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0.7, 1], ["0%", "-12%"]);
  return (
    <section data-testid="final-cta" className="relative overflow-hidden py-28 md:py-40">
      <motion.div style={reduce ? {} : { y }} className="absolute inset-0 -z-10">
        <img src="https://images.unsplash.com/photo-1734888369502-3e01d4c0a46e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" aria-hidden="true" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-[#141414]/85" />
      </motion.div>
      <div className="absolute inset-0 -z-10 blueprint-grid opacity-40" aria-hidden="true" />
      <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
        <Reveal className="flex justify-center"><Eyebrow>Preventivo immediato</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#F5F1EA] mt-6 leading-[1.02] text-balance">
            Hai bisogno? <br className="hidden sm:block" />
            <span className="italic text-[#C89B4A]">Chiamaci adesso.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg text-[#F5F1EA]/75 max-w-xl mx-auto">
            Rispondiamo il prima possibile con un preventivo chiaro e completo.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Magnetic strength={0.25}>
              <a href={BUSINESS.phoneHref} data-testid="final-call" className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-[#B5502F] px-8 py-4 text-base font-semibold text-[#F5F1EA] hover:bg-[#c95c39] transition-colors">
                <Phone size={20} strokeWidth={1.75} /> {BUSINESS.phone}
              </a>
            </Magnetic>
            <Magnetic strength={0.25}>
              <a href={`mailto:${BUSINESS.email}`} data-testid="final-email" className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-[#C89B4A] px-8 py-4 text-base font-semibold text-[#141414] hover:bg-[#d8ab5a] transition-colors">
                <span className="grid h-7 w-7 place-items-center rounded-md bg-white shrink-0">
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#4caf50" d="M45,16.2l-5,2.75l-5,4.75L35,40h7c1.657,0,3-1.343,3-3V16.2z"/>
                    <path fill="#1e88e5" d="M3,16.2l3.614,1.71L13,23.7V40H6c-1.657,0-3-1.343-3-3V16.2z"/>
                    <polygon fill="#e53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.95 35,23.7 36,17"/>
                    <path fill="#c62828" d="M3,12.298V16.2l10,7.5V11.2L9.876,8.859C9.132,8.301,8.228,8,7.298,8h0C4.924,8,3,9.924,3,12.298z"/>
                    <path fill="#fbc02d" d="M45,12.298V16.2l-10,7.5V11.2l3.124-2.341C38.868,8.301,39.772,8,40.702,8h0C43.076,8,45,9.924,45,12.298z"/>
                  </svg>
                </span>
                Manda una mail
              </a>
            </Magnetic>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Contact form ---------------- */
const EMPTY = { nome: "", citta: "", telefono: "", email: "", servizio: "Seleziona servizio", messaggio: "" };

export function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 2) e.nome = "Inserisci il tuo nome";
    if (form.citta.trim().length < 2) e.citta = "Inserisci la città";
    if (form.telefono.trim().length < 5) e.telefono = "Inserisci un telefono valido";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Inserisci un'email valida";
    if (form.messaggio.trim().length < 5) e.messaggio = "Scrivi due righe sul lavoro";
    if (form.servizio === "Seleziona servizio") e.servizio = "Seleziona un servizio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("done");
      toast.success("Ti ringraziamo di averci contattato, il nostro ufficio ti ricontatterà a breve!", { duration: 5000 });
      setForm(EMPTY);
      setTimeout(() => setStatus("idle"), 2600);
    } catch (err) {
      setStatus("idle");
      toast.error("Qualcosa è andato storto. Chiamaci pure allo " + BUSINESS.phone);
    }
  };

  const field = "w-full bg-transparent border-b border-[#F5F1EA]/20 py-3 text-[#F5F1EA] placeholder-[#F5F1EA]/35 focus:border-[#C89B4A] focus:outline-none transition-colors";

  return (
    <section id="contatti" data-testid="contact-section" className="relative py-24 md:py-32 bg-[#1c1c1c] border-t border-[#F5F1EA]/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <Reveal><Eyebrow>Contatti</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5 text-balance">
              Raccontaci di cosa hai bisogno
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-[#F5F1EA]/65 leading-relaxed">
              Lasciaci un messaggio: ti risponderemo non appena possibile! Per le emergenze scrivi una mail o chiama direttamente.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            {[
              { icon: Phone, label: BUSINESS.phone, href: BUSINESS.phoneHref, testid: "contact-phone" },
              { icon: Mail, label: BUSINESS.email, href: `mailto:${BUSINESS.email}`, testid: "contact-email" },
              { icon: MapPin, label: BUSINESS.address, href: "#zona", testid: "contact-address" },
              { icon: Clock, label: BUSINESS.hours, testid: "contact-hours" },
            ].map((c, i) => {
              const Inner = (
                <div className="flex items-center gap-4">
                  <span className="grid h-12 w-12 place-items-center rounded-full border border-[#C89B4A]/30 text-[#C89B4A] shrink-0">
                    <c.icon size={18} strokeWidth={1.5} />
                  </span>
                  <span className="text-[#F5F1EA]/85">{c.label}</span>
                </div>
              );
              return c.href ? (
                <a key={i} href={c.href} data-testid={c.testid} className="block hover:text-[#C89B4A] transition-colors">{Inner}</a>
              ) : (
                <div key={i} data-testid={c.testid}>{Inner}</div>
              );
            })}
          </div>

          <div className="mt-8 flex items-center gap-4" data-testid="contact-socials">
            <span className="text-xs uppercase tracking-widest text-[#F5F1EA]/50">Seguici</span>
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram di Officine Galloni"
              data-testid="social-instagram"
              className="grid h-11 w-11 place-items-center rounded-full border border-[#F5F1EA]/15 text-[#F5F1EA]/80 hover:text-[#141414] hover:bg-[#C89B4A] hover:border-[#C89B4A] hover:-translate-y-1 transition-all duration-300"
            >
              <Instagram size={18} strokeWidth={1.75} />
            </a>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook di Officine Galloni"
              data-testid="social-facebook"
              className="grid h-11 w-11 place-items-center rounded-full border border-[#F5F1EA]/15 text-[#F5F1EA]/80 hover:text-[#141414] hover:bg-[#C89B4A] hover:border-[#C89B4A] hover:-translate-y-1 transition-all duration-300"
            >
              <Facebook size={18} strokeWidth={1.75} />
            </a>
            <a
              href={BUSINESS.tiktok}
              target="_blank"
              rel="noreferrer"
              aria-label="TikTok di Officine Galloni"
              data-testid="social-tiktok"
              className="grid h-11 w-11 place-items-center rounded-full border border-[#F5F1EA]/15 text-[#F5F1EA]/80 hover:text-[#141414] hover:bg-[#C89B4A] hover:border-[#C89B4A] hover:-translate-y-1 transition-all duration-300"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.5 3c.4 2.1 1.6 3.6 3.6 4v2.6c-1.3.1-2.5-.3-3.6-.9v5.9c0 3.6-2.4 6-5.7 6C7.3 22.6 5 20.2 5 16.9c0-3.1 2.4-5.6 5.5-5.4v2.7c-.4-.1-.8-.2-1.2-.1-1.3.2-2.1 1.2-2 2.6.1 1.3 1.1 2.2 2.4 2.1 1.4-.1 2.2-1.1 2.2-2.7V3h2.6z"/>
              </svg>
            </a>
          </div>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="metal-border rounded-2xl p-7 md:p-9 space-y-6" noValidate>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#F5F1EA]/50">Nome e cognome</label>
                <input data-testid="input-nome" className={field} value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Mario Rossi" />
                {errors.nome && <p className="mt-1.5 text-xs text-[#B5502F]" data-testid="error-nome">{errors.nome}</p>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#F5F1EA]/50">Città</label>
                <input data-testid="input-citta" className={field} value={form.citta} onChange={(e) => set("citta", e.target.value)} placeholder="Montespertoli" />
                {errors.citta && <p className="mt-1.5 text-xs text-[#B5502F]" data-testid="error-citta">{errors.citta}</p>}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#F5F1EA]/50">Telefono</label>
                <input data-testid="input-telefono" className={field} value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+39 ..." />
                {errors.telefono && <p className="mt-1.5 text-xs text-[#B5502F]" data-testid="error-telefono">{errors.telefono}</p>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#F5F1EA]/50">Email</label>
                <input data-testid="input-email" className={field} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mail@esempio.it" />
                {errors.email && <p className="mt-1.5 text-xs text-[#B5502F]" data-testid="error-email">{errors.email}</p>}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#F5F1EA]/50">Tipo di servizio</label>
              <select data-testid="input-servizio" className={`${field} [&>option]:bg-[#1c1c1c]`} value={form.servizio} onChange={(e) => set("servizio", e.target.value)}>
                <option value="Seleziona servizio">Seleziona servizio</option>
                {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.servizio && <p className="mt-1.5 text-xs text-[#B5502F]" data-testid="error-servizio">{errors.servizio}</p>}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#F5F1EA]/50">Messaggio</label>
              <textarea data-testid="input-messaggio" rows={4} className={`${field} resize-none`} value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)} placeholder="Descrivi brevemente di cosa hai bisogno..." />
              {errors.messaggio && <p className="mt-1.5 text-xs text-[#B5502F]" data-testid="error-messaggio">{errors.messaggio}</p>}
            </div>
            <button
              type="submit"
              data-testid="submit-lead"
              disabled={status !== "idle"}
              className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#C89B4A] px-8 py-4 text-base font-semibold text-[#141414] hover:bg-[#d8ab5a] transition-colors disabled:opacity-80"
            >
              <AnimatePresence mode="wait" initial={false}>
                {status === "idle" && <motion.span key="i" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="inline-flex items-center gap-2.5"><Send size={18} strokeWidth={1.75} /> Invia richiesta</motion.span>}
                {status === "loading" && <motion.span key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="inline-flex items-center gap-2.5"><Loader2 size={18} className="animate-spin" /> Invio...</motion.span>}
                {status === "done" && <motion.span key="d" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="inline-flex items-center gap-2.5"><Check size={18} strokeWidth={2.5} /> Inviato</motion.span>}
              </AnimatePresence>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
export function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-[#F5F1EA]/10 py-14">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row gap-10 md:justify-between">
          <div className="max-w-sm">
            <p className="font-display text-2xl font-extrabold tracking-tight text-[#F5F1EA]">
              Officine <span className="text-[#C89B4A]">Galloni</span>
            </p>
            <p className="mt-3 text-sm text-[#F5F1EA]/55 leading-relaxed">
              Fabbri-serramentisti a Montespertoli dal 1979. Un mestiere che si tramanda da 3 generazioni e che viene svolto con tutta l'esperienza e l'abilità che ha un'officina di alto livello che lavora da più di 47 anni in questo settore.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-10 text-sm">
            <div className="space-y-3">
              <p className="eyebrow">Sezioni</p>
              <a href="#servizi" className="block text-[#F5F1EA]/70 hover:text-[#C89B4A] transition-colors">Servizi</a>
              <a href="#lavori" className="block text-[#F5F1EA]/70 hover:text-[#C89B4A] transition-colors">Lavori</a>
              <a href="#contatti" className="block text-[#F5F1EA]/70 hover:text-[#C89B4A] transition-colors">Contatti</a>
            </div>
            <div className="space-y-3">
              <p className="eyebrow">Contatti</p>
              <a href={BUSINESS.phoneHref} className="block text-[#F5F1EA]/70 hover:text-[#C89B4A] transition-colors">{BUSINESS.phone}</a>
              <a href={`mailto:${BUSINESS.email}`} className="block text-[#F5F1EA]/70 hover:text-[#C89B4A] transition-colors">{BUSINESS.email}</a>
              <p className="text-[#F5F1EA]/70">{BUSINESS.address}</p>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-[#F5F1EA]/10 flex flex-col sm:flex-row gap-3 justify-between text-xs text-[#F5F1EA]/45">
          <p>© {new Date().getFullYear()} Officine Galloni · {BUSINESS.piva}</p>
          <p className="flex items-center gap-4">
            <a href="#" className="hover:text-[#C89B4A] transition-colors">Privacy</a>
            <a href="#" className="hover:text-[#C89B4A] transition-colors">Cookie</a>
            <span>Made with care</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- Floating actions + back to top + cookie ---------------- */
export function FloatingActions() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <a
        href={BUSINESS.whatsapp}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp"
        aria-label="Scrivici su WhatsApp"
        className="fixed bottom-5 right-5 z-[150] grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-xl hover:scale-105 transition-transform"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
        <MessageCircle size={26} strokeWidth={1.75} className="relative" />
      </a>
      <AnimatePresence>
        {show && (
          <motion.button
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="back-to-top"
            aria-label="Torna su"
            className="fixed bottom-24 right-5 z-[150] grid h-12 w-12 place-items-center rounded-full border border-[#C89B4A]/40 bg-[#1c1c1c]/90 backdrop-blur text-[#C89B4A] hover:bg-[#C89B4A] hover:text-[#141414] transition-colors"
          >
            <ArrowUp size={20} strokeWidth={1.75} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("og-cookie")) setTimeout(() => setOpen(true), 1200);
  }, []);
  const accept = () => { localStorage.setItem("og-cookie", "1"); setOpen(false); };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 z-[160] max-w-sm rounded-2xl border border-[#F5F1EA]/10 bg-[#1c1c1c]/95 backdrop-blur-xl p-5 shadow-2xl"
        >
          <p className="text-sm text-[#F5F1EA]/75 leading-relaxed">
            Usiamo cookie tecnici per farti navigare al meglio. Continuando accetti la nostra policy.
          </p>
          <div className="mt-4 flex gap-3">
            <button onClick={accept} data-testid="cookie-accept" className="rounded-full bg-[#C89B4A] px-5 py-2 text-sm font-semibold text-[#141414] hover:bg-[#d8ab5a] transition-colors">Accetto</button>
            <button onClick={() => setOpen(false)} className="rounded-full px-4 py-2 text-sm text-[#F5F1EA]/60 hover:text-[#F5F1EA] transition-colors">Solo necessari</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
